import { z } from 'zod'
import { useSyncedState } from './useSyncedState'
import {
  computeEffectiveBucket,
  deriveDefaultDots,
} from '~/components/ReviewPage/granularityGovernance'

// 'fy' is in the PARSER unconditionally; only the PICKER is gated (GranularityControls).
// A vodafone-authored URL replayed on another tenant must not silently fall back to 'day'
// (useSyncedState:67-70 warns + defaults). On a January fiscal year it renders as calendar year,
// which is truthful.
const granularityUnitSchema = z.enum(['day', 'week', 'month', 'quarter', 'year', 'fy'])
const granularityDotsSchema = z.coerce.number().int().min(2).max(182)

export type GranularityUnit = z.infer<typeof granularityUnitSchema>

export interface GranularityState {
  unit: GranularityUnit
  dots: number
  setUnit: (u: GranularityUnit) => void
  setDots: (n: number) => void
}

export function useGranularityState(timeframe?: {
  fromIso: string
  toIso: string
}): GranularityState {
  const [unit, setUnit] = useSyncedState('gran_unit', granularityUnitSchema, 'url', 'day')
  // Row 14 blocks-first default: with the active timeframe, default = ceil(buckets/4) at the
  // GOVERNED unit (coarsening changes the bucket count the panels actually partition).
  // Timeframe-less callers (ChartFromPin replay → Home/pin tiles) keep the legacy 91 — a pinned
  // tile stays single-panel (D5 Home carve-out). Explicit gran_dots in the URL always wins.
  const effectiveUnit = timeframe
    ? computeEffectiveBucket(timeframe.fromIso, timeframe.toIso, unit)
    : unit
  const defaultDots = timeframe
    ? deriveDefaultDots(timeframe.fromIso, timeframe.toIso, effectiveUnit)
    : 91
  const [dots, setDotsRaw] = useSyncedState('gran_dots', granularityDotsSchema, 'url', defaultDots)
  return { unit, dots, setUnit, setDots: setDotsRaw }
}
